import React, { useState, useMemo } from 'react';
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm';
import './Tabs.scss';
import { Bookmark } from '../api/note';
import { useTranslation } from 'react-i18next';

/* 把 audio 跳转封装一下，Tabs 内部直接调用即可  */
const seekAudio = (sec:number) => {
  const audio = document.querySelector<HTMLAudioElement>('audio');
  if (audio) {
    audio.currentTime = sec;
    /* 如果想跳过去就播放，可解除注释 */
    // if (audio.paused) audio.play();
  }
};

interface Props {
  summary: string;
  transcription: string;
  insight?: Bookmark[];
}

type TabKey = 'summary' | 'trans' | 'insight';
const Tabs: React.FC<Props> = ({ summary, transcription, insight = [] }) => {
  /* ---------------- 现有逻辑 ---------------- */
  const [tab, setTab] = useState<TabKey>('summary');

  const { t } = useTranslation();

  const parsedTrans = useMemo(
    () =>
      transcription
        .split(/([.?!])/)
        .reduce<string[]>((arr, cur) => {
          if (['.', '?', '!'].includes(cur)) {
            arr[arr.length - 1] += cur;
          } else if (cur.trim()) arr.push(cur.trim());
          return arr;
        }, []),
    [transcription]
  );

  /* ---------------- Insight 逻辑 ---------------- */
  // 1. 当 time===0 时保持接口顺序并且不展示时间
  // 2. 其余 insight 按 time 升序
  const parsedInsight = useMemo(() => {
    if (!insight.length) return [];

    const withTime   = insight.filter(i => i.time > 0).sort((a, b) => a.time - b.time);
    const noTimeList = insight.filter(i => i.time === 0); // 保持原顺序
    return [...withTime, ...noTimeList];
  }, [insight]);

  const formatTime = (sec: number) => {
    const m = Math.floor(sec / 60)
      .toString()
      .padStart(2, '0');
    const s = (sec % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };
  return (
    <section className="tabs">
      {/* ----------- tab header（保持不变） ----------- */}
      <div className="tab-nav">
        <div
          className={tab === 'summary' ? 'active' : ''}
          onClick={() => setTab('summary')}
        >
          {t('SumMailAI_1_0_0_web_summary')}
        </div>
        <div
          className={tab === 'trans' ? 'active' : ''}
          onClick={() => setTab('trans')}
        >
          {t('SumMailAI_1_0_0_web_transcription')}
        </div>
        {insight.length > 0 && (
          <div
            className={tab === 'insight' ? 'active' : ''}
            onClick={() => setTab('insight')}
          >
            {t('SumMailAI_1_0_0_web_bookmark')}
          </div>
        )}
      </div>

      <div className="tab-body">
        {/* ---------- Summary ---------- */}
        {tab === 'summary' && (
          <ReactMarkdown
            remarkPlugins={[remarkGfm as any]}
            components={{
              h1: ({ node, ...props }) => <h2 {...props} />,
              h2: ({ node, ...props }) => <h3 {...props} />,
              a:  ({ node, ...props }) => <a target="_blank" rel="noreferrer" {...props} />
            }}
          >
            {summary}
          </ReactMarkdown>
        )}

        {/* ---------- Transcription ---------- */}
        {tab === 'trans' && (
          <article className="trans-body">
            {parsedTrans.map((line, i) => (
              <p key={i}>{line}</p>
            ))}
          </article>
        )}

        {/* ---------- Insight（文本 / 图片） ---------- */}
        {tab === 'insight' && (
          <article className="insight-body">
            {parsedInsight.map((item, idx) => (
              <p key={idx}>
                {/* time > 0 时展示可点击时间戳 */}
                {item.time > 0 && (
                  <span
                    className="time-tag"
                    onClick={() => seekAudio(item.time)}
                  >
                    {formatTime(item.time)}
                  </span>
                )}

                {/* 根据 type 渲染文本或图片 */}
                {item.type === 1 ? (
                  <span>{item.text}</span>
                ) : (
                  <img className="insight-img" src={item.text} alt="insight" />
                )}
              </p>
            ))}
          </article>
        )}
      </div>
    </section>
  );
};

export default Tabs;

